/* eslint react-hooks/exhaustive-deps: "warn" */
import { type ReactNode, useEffect, useRef, useState } from 'react';

import cn from 'classnames';

import { useInt } from 'hooks/useInt';

import { TitleBlock } from 'ui/atoms/TitleBlock/TitleBlock';

import styles from './SalesProspectingLeadGenerationProblem.module.scss';

type Translate = (key: string) => string;

type Problem = {
  idx: string;
  tag: string;
  title: string;
  bullets: ReactNode[];
  answer: string;
};

const getProblems = (t: Translate): Problem[] => [
  {
    idx: '01',
    tag: 'sourcing',
    title: t('Most vendors start with social media'),
    bullets: [
      t('The record begins as a scraped profile, mainly LinkedIn.'),
      t('An email and a phone number are added on top.'),
      t('Nothing underneath it came from a register.'),
    ],
    answer: t('We start at the register. The company record is the filing itself, and the contact is attached to it.'),
  },
  {
    idx: '02',
    tag: 'existence',
    title: t('A profile is not proof that a company exists'),
    bullets: [
      t('Pages outlive dissolution.'),
      t('Pages exist for entities that never traded.'),
      t('The seller cannot tell which is which.'),
    ],
    answer: t(
      'Status comes from the register, with the date it changed: active, dissolved, struck off, in liquidation.',
    ),
  },
  {
    idx: '03',
    tag: 'classification',
    title: t('The industry classification is generic and self-declared'),
    bullets: [
      t('One global dropdown, one selection per company.'),
      t('Editable at any time by whoever owns the page.'),
      t('Whole markets collapse into a handful of buckets.'),
    ],
    answer: t(
      'We carry the code the registrar assigned, in the scheme that market uses, with NACE and NAICS mapped alongside.',
    ),
  },
  {
    idx: '04',
    tag: 'revenue',
    title: t('Revenue is an estimate dressed as a fact'),
    bullets: [
      t('Headcount inferred from how many people list the company.'),
      t('Multiplied by a sector average.'),
      t('Presented with no label saying it is an estimate.'),
    ],
    answer: t(
      'Filed accounts appear as filed, with the filing date. Modelled fields are labelled modelled, every time.',
    ),
  },
  {
    idx: '05',
    tag: 'coverage',
    title: t('Not every company is on LinkedIn'),
    bullets: [
      <>
        {t('About')} <b>60 million</b> {t('companies have a LinkedIn page.')}
      </>,
      <>
        {t('More than')} <b>600 million</b> {t('are on a government register.')}
      </>,
      <>
        {t('The other')} <b>540 million</b> {t('never appear in a social-sourced list.')}
      </>,
    ],
    answer: t(
      'Our universe is the register: every entity a government has incorporated, whether or not it markets itself.',
    ),
  },
];

export const SalesProspectingLeadGenerationProblem = () => {
  const { t } = useInt();
  const problems = getProblems(t);
  const blockRefs = useRef<(HTMLElement | null)[]>([]);
  const [active, setActive] = useState(0);

  useEffect(() => {
    const reduced = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

    if (reduced) {
      return undefined;
    }

    let frame: number;
    let last = -1;

    const track = () => {
      const mid = window.innerHeight / 2;
      let best = 0;
      let bestDistance = Infinity;

      blockRefs.current.forEach((node, index) => {
        if (!node) {
          return;
        }

        const rect = node.getBoundingClientRect();
        const distance = Math.abs((rect.top + rect.bottom) / 2 - mid);

        if (distance < bestDistance) {
          bestDistance = distance;
          best = index;
        }
      });

      if (best !== last) {
        last = best;
        setActive(best);
      }

      frame = requestAnimationFrame(track);
    };

    frame = requestAnimationFrame(track);

    return () => cancelAnimationFrame(frame);
  }, []);

  return (
    <section className={styles.section}>
      <div className={styles.container}>
        <div className={styles.marker}>
          <span className={styles.markerNum}>01</span> / {t('Problem')}
        </div>

        <div className={styles.body}>
          <div className={styles.head}>
            <TitleBlock
              align="left"
              title={t('Your addressable market is not on LinkedIn')}
              description={t(
                'Most prospecting platforms are built on scraped social-media profiles with an email and a phone number added on top. That is a directory of the companies that chose to market themselves. It is not a record of the companies that exist.',
              )}
            />
          </div>

          <div className={styles.rows}>
            <div className={styles.rail}>
              <div className={styles.cards}>
                {problems.map((problem, index) => (
                  <div
                    key={problem.tag}
                    className={cn(styles.card, index === active && styles.cardOn)}
                    aria-hidden="true"
                  >
                    <span className={styles.idx}>{problem.idx}</span>
                    <div className={styles.chapter}>{problem.title}</div>
                    <span className={styles.pill}>{t(problem.tag)}</span>
                  </div>
                ))}
              </div>

              <div className={styles.ticks}>
                {problems.map((problem, index) => (
                  <span key={problem.tag} className={cn(styles.tick, index === active && styles.tickOn)} />
                ))}
              </div>
            </div>

            <div className={styles.stack}>
              {problems.map((problem, index) => (
                <article
                  key={problem.tag}
                  ref={(node) => {
                    blockRefs.current[index] = node;
                  }}
                  className={cn(styles.block, index === active && styles.blockOn)}
                >
                  <div className={styles.bhead}>
                    <span className={styles.idx}>{problem.idx}</span>
                    <h3>{problem.title}</h3>
                    <span className={styles.pill}>{t(problem.tag)}</span>
                  </div>

                  <ul className={styles.list}>
                    {problem.bullets.map((bullet, bulletIndex) => (
                      <li key={bulletIndex}>{bullet}</li>
                    ))}
                  </ul>

                  <p className={styles.answer}>{problem.answer}</p>
                </article>
              ))}
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};
